// MobileRoot — raiz da SUPERFÍCIE MOBILE (touch-first).
//
// A bifurcação acontece no RootRoute (`if (resolveSurface() === 'mobile')`). Esta
// é a porta de entrada das telas de celular/tablet.
//
// Parede A1 (login mobile): decide entre AUTH (não logado) e LOGADO:
//  - SEM sessão Swipe → MobileAuthFlow (login/signup/recuperação).
//  - COM sessão Swipe → SHELL mobile (parede A3): tab bar + Home com rails.
// O objetivo da A1 é o LOGIN funcionar e PERSISTIR a sessão Swipe (o motor
// account-auth faz isso). A fonte Xtream / conteúdo são paredes posteriores.
//
// Parede A3 (shell + Home): substitui o antigo placeholder "logado" pelo
// MobileShell real — bottom tab bar + Home navegável com rails touch. Player
// (A4) e telas completas das outras abas/busca (A5+) ainda são placeholders.
//
// Parede A2 (onboarding mobile): ANTES do login, no 1º acesso (welcome nunca
// visto) E sem sessão → MobileOnboarding (3 slides com swipe). Ao terminar/pular,
// marca o welcome como visto e cai no MobileAuthFlow. O flag é o MESMO da TV
// (`welcome-flag.ts` / `iptv:welcome-seen`), por decisão do desenho (§2.1): é só
// um booleano "já viu o intro", agnóstico de plataforma — quem viu na TV não revê
// no celular e vice-versa.
//
// ISOLAMENTO (regra da frente mobile): este arquivo e toda a árvore mobile NÃO
// importam Norigin / spatial-navigation / useFocusable. Mobile é touch puro —
// tap/scroll nativo, sem foco de controle remoto.

import { useEffect, useState } from 'react'

import { bootstrapMobileSync, bumpMobileSyncEpoch } from './mobile/mobile-sync-bootstrap'
import { MobileAuthFlow } from './mobile/MobileAuthFlow'
import { MobileOnboarding } from './mobile/MobileOnboarding'
import { MobileShell } from './mobile/MobileShell'
import { useSessionStore } from './store'
import { endSwipeSync, hasSwipeSession, onSwipeIdentityChange } from './sync'
import { hasSeenWelcome, markWelcomeSeen } from './welcome-flag'

export function MobileRoot(): JSX.Element {
  // Lê a sessão Swipe no 1º render; `authed` reflete a transição pós-login dentro
  // da mesma montagem (o motor já persistiu a sessão antes de chamar onAuthenticated).
  const [authed, setAuthed] = useState<boolean>(() => hasSwipeSession())

  // SYNC cross-device (10/10/2026): a cada entrada no estado logado — o boot com
  // sessão E a transição pós-login (e-mail/Google, todos passam pelo
  // `onAuthenticated`) — hidrata o contexto de sync pela fonte da conta, adota os
  // perfis dela e puxa o histórico. Sem isto o celular não sincronizava NADA (ver
  // mobile/mobile-sync-bootstrap.ts). Best-effort: não bloqueia o shell.
  useEffect(() => {
    if (authed) void bootstrapMobileSync()
  }, [authed])

  // Sessão REVOGADA com o celular aberto (decisão do Maestro, coerente com a do dono pra
  // TV, rodada 5): derruba na hora pelo MESMO caminho da TV — o contexto de conta cai
  // (`dropAccountContext`) e o celular volta ao login, desmontando o shell (e o player
  // junto). Só chega aqui uma revogação DEFINITIVA: a sessão só é limpa por um 401/403
  // do refresh com código de revogação (`DEFINITIVE_REFRESH_ERRORS`, swipe-session.ts);
  // rede, 5xx e 429 não limpam e não passam por aqui.
  useEffect(
    () =>
      onSwipeIdentityChange((user) => {
        if (user !== null) return
        bumpMobileSyncEpoch()
        useSessionStore.getState().dropAccountContext()
        setAuthed(false)
      }),
    [],
  )

  // Logout do Perfil (A7): encerra a sessão Swipe de forma idêntica à TV
  // (endSwipeSync com flush final + session.logout) e volta pro login flipando
  // `authed` pra false — o MobileRoot re-renderiza no MobileAuthFlow sem deixar
  // estado órfão (sessão Swipe limpa do disco, store de sessão zerado). NÃO usa
  // react-router (mobile é shell por estado, não rotas).
  const handleLogout = (): void => {
    // ANTES de qualquer limpeza: invalida o bootstrap do sync em voo, senão a resposta
    // dele regrava a conta e o perfil de quem está saindo (cybersec P1, caminho b).
    bumpMobileSyncEpoch()
    // Flush final do pendente (não perder progresso) + encerra TODO o estado de
    // sync (sessão Swipe + outbox + cache). Fire-and-forget, igual à TV.
    void endSwipeSync({ flushFirst: true })
    useSessionStore.getState().logout('user')
    setAuthed(false)
  }

  // COM sessão → SHELL mobile (A3). O onboarding não se aplica a quem já tem conta.
  if (authed) {
    return <MobileShell onLogout={handleLogout} />
  }

  // SEM sessão: 1º acesso (welcome nunca visto) mostra o onboarding ANTES do
  // login. Quem já viu (na TV ou aqui) pula direto pro login. Estado vive aqui
  // pra a transição "terminou onboarding → login" acontecer na mesma montagem.
  return <MobileUnauthenticated onAuthenticated={() => setAuthed(true)} />
}

/** Estado NÃO-LOGADO do mobile: onboarding (1º acesso) → login. Separado pra
 *  manter o gate do welcome num componente próprio (hooks só rodam quando não há
 *  sessão; o caminho logado nem monta isto). */
function MobileUnauthenticated({
  onAuthenticated,
}: {
  onAuthenticated: () => void
}): JSX.Element {
  // Onboarding pendente = welcome nunca visto (lido no 1º render). Ao concluir,
  // marca visto e troca pra false → renderiza o login na mesma montagem.
  const [onboarding, setOnboarding] = useState<boolean>(() => !hasSeenWelcome())

  if (onboarding) {
    return (
      <MobileOnboarding
        onDone={() => {
          markWelcomeSeen()
          setOnboarding(false)
        }}
      />
    )
  }

  return <MobileAuthFlow onAuthenticated={onAuthenticated} />
}
